import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { SHAYARI, CATEGORIES } from '../data/shayari';

// ─── Intent Engine ───────────────────────────────────────────────────────────

const CATEGORY_KEYWORDS = {
  love:         ['love','pyar','mohabbat','ishq','प्यार','मोहब्बत','इश्क','محبت','عشق'],
  sad:          ['sad','dukh','dard','gham','उदास','दुख','दर्द','غم','درد','udaas'],
  motivational: ['motivat','himmat','hausla','josh','हिम्मत','हौसला','حوصلہ','inspire','motivat'],
  romantic:     ['romantic','romance','shayar','dilruba','दिलरुबा','رومانٹک'],
  breakup:      ['breakup','judai','bichhad','जुदाई','بچھڑ','ala'],
  friendship:   ['friend','dost','yaar','dosti','दोस्त','यार','دوست'],
  nature:       ['nature','prakriti','jungle','flower','tree','प्रकृति','جنگل','phal'],
  patriotic:    ['desh','watan','bharat','india','वतन','देश','وطن','patriot'],
  attitude:     ['attitude','style','swagger','bold','अंदाज','انداز'],
  ghazal:       ['ghazal','ग़ज़ल','غزل'],
  nazm:         ['nazm','नज़्म','نظم','poem'],
  dohe:         ['dohe','doha','दोहे','दोहा','kabir'],
  twolines:     ['two line','2 line','short','do line','दो लाइन','chota','छोटा'],
  life:         ['life','zindagi','जिंदगी','زندگی','jeena'],
  miss:         ['miss','yaad','याद','یاد','tujhe yaad','missing'],
  hearttouching:['heart','dil','touching','dil ko','दिल','دل'],
};

const MOOD_MAP = {
  happy:     ['love','romantic','friendship','motivational'],
  sad:       ['sad','breakup','miss','hearttouching'],
  inspired:  ['motivational','life','patriotic'],
  angry:     ['attitude','breakup'],
  nostalgic: ['miss','hearttouching','dohe'],
  calm:      ['nature','ghazal','nazm'],
  proud:     ['patriotic','attitude','motivational'],
};

const POET_KEYWORDS = {
  'Mirza Ghalib':        ['ghalib','ग़ालिब','غالب'],
  'Faiz Ahmad Faiz':     ['faiz','فیض'],
  'Kabir Das':           ['kabir','कबीर','کبیر'],
  'Rahim Das':           ['rahim','रहीम','رحیم'],
  'Allama Iqbal':        ['iqbal','इक़बाल','اقبال'],
  'Gulzar':              ['gulzar','गुलज़ार','گلزار'],
  'Sahir Ludhianvi':     ['sahir','साहिर','ساحر'],
  'Ramdhari Singh Dinkar':['dinkar','दिनकर'],
  'Sumitranandan Pant':  ['pant','पंत'],
};

const OCCASION_MAP = {
  birthday:  ['birthday','janamdin','जन्मदिन','سالگرہ'],
  wedding:   ['wedding','shaadi','shadi','शादी','شادی'],
  eid:       ['eid','ईद','عید'],
  diwali:    ['diwali','deepawali','दीपावली','دیوالی'],
  holi:      ['holi','होली','ہولی'],
  mothers:   ["mother's day",'maa','माँ','ماں','mom'],
  fathers:   ["father's day",'papa','pita','पिता','باپ'],
  independence:['independence','15 august','स्वतंत्रता','آزادی'],
};

const QUICK_CHIPS = [
  { label:'❤️ Love Shayari',  msg:'show me love shayari' },
  { label:'💔 Sad Shayari',   msg:'sad shayari dikhao' },
  { label:'🌟 Motivational',  msg:'motivational shayari' },
  { label:'🎭 Ghazal',        msg:'ghazal sunao' },
  { label:'✍️ Random',        msg:'ek random shayari dena' },
  { label:'👨‍🎨 By Poet',       msg:'Ghalib ki shayari' },
  { label:'🌿 Nature',        msg:'nature shayari' },
  { label:'🇮🇳 Patriotic',     msg:'desh bhakti shayari' },
  { label:'📜 Dohe',          msg:'kabir ke dohe' },
  { label:'😊 Happy Mood',    msg:'I am happy, suggest shayari' },
];

function detectIntent(input) {
  const q = input.toLowerCase().trim();

  if (/^(hi|hello|helo|hii|hey|namaste|salam|adaab|नमस्ते|سلام)/i.test(q))
    return { type: 'greet' };

  if (/random|koi bhi|any|ek|एक|کوئی/.test(q) && !/category|category/.test(q))
    return { type: 'random' };

  if (/help|kya kar|features|kya kya|क्या|کیا/.test(q))
    return { type: 'help' };

  if (/all categor|sabhi|सभी|تمام|list categor/.test(q))
    return { type: 'categories' };

  if (/popular|trending|most liked|famous|مشہور|प्रसिद्ध/.test(q))
    return { type: 'popular' };

  if (/search|find|dhundho|ढूंढ|ڈھونڈ/.test(q))
    return { type: 'search', query: q.replace(/search|find|dhundho/gi, '').trim() };

  // Mood detection
  for (const [mood, cats] of Object.entries(MOOD_MAP)) {
    if (q.includes(mood) || q.includes(`feel ${mood}`) || q.includes(`${mood} mood`))
      return { type: 'mood', mood, categories: cats };
  }

  // Occasion
  for (const [occ, kws] of Object.entries(OCCASION_MAP)) {
    if (kws.some(k => q.includes(k)))
      return { type: 'occasion', occasion: occ };
  }

  // Poet
  for (const [poet, kws] of Object.entries(POET_KEYWORDS)) {
    if (kws.some(k => q.includes(k)))
      return { type: 'poet', poet };
  }

  // Category
  for (const [cat, kws] of Object.entries(CATEGORY_KEYWORDS)) {
    if (kws.some(k => q.includes(k)))
      return { type: 'category', category: cat };
  }

  // Explain
  if (/matlab|arth|explain|meaning|samjhao|क्या मतलब|کیا مطلب/.test(q))
    return { type: 'explain', query: q };

  return { type: 'search', query: q };
}

function pickRandom(arr, n = 3) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

function buildResponse(intent, language) {
  const lang = language || 'hi';

  switch (intent.type) {
    case 'greet':
      return {
        text: lang === 'hi'
          ? '🌙 आदाब! मैं आपका शायरी सहायक हूँ।\nआप मुझसे पूछ सकते हैं:\n• किसी भी category की शायरी\n• किसी शायर की रचनाएँ\n• मूड के अनुसार शायरी\n• Random शायरी\n\nबताइए, आज कैसी शायरी चाहिए? 💫'
          : lang === 'ur'
          ? '🌙 آداب! میں آپ کا شاعری معاون ہوں۔\nآپ مجھ سے پوچھ سکتے ہیں:\n• کسی بھی category کی شاعری\n• کسی شاعر کی نظمیں\n• موڈ کے مطابق شاعری\n\nبتائیں، آج کیسی شاعری چاہیے؟ 💫'
          : '🌙 Adaab! I am your Shayari assistant.\nYou can ask me:\n• Shayari by any category\n• Poems by any poet\n• Shayari by mood\n• Random shayari\n\nWhat kind of shayari do you want today? 💫',
        shayaris: [],
        chips: QUICK_CHIPS.slice(0, 6),
      };

    case 'help':
      return {
        text: '🤖 **मैं यह कर सकता हूँ:**\n\n📚 Category से शायरी\n👤 शायर की शायरी\n😊 मूड से शायरी\n🎲 Random शायरी\n🔍 Keyword search\n🎉 Occasion शायरी\n❤️ Favorites में जोड़ें\n📋 Copy करें\n📱 Share करें',
        shayaris: [],
        chips: QUICK_CHIPS,
      };

    case 'categories': {
      const cats = CATEGORIES.map(c => `${c.icon} ${c.name[lang] || c.name.en}`).join('\n');
      return {
        text: `📚 **सभी Categories:**\n\n${cats}\n\nकिसी भी category का नाम लिखें!`,
        shayaris: [],
        chips: CATEGORIES.slice(0, 6).map(c => ({
          label: `${c.icon} ${c.name[lang] || c.name.en}`,
          msg: c.name.en.toLowerCase() + ' shayari',
        })),
      };
    }

    case 'category': {
      const pool = SHAYARI.filter(s => s.category === intent.category);
      const picks = pickRandom(pool, 3);
      const catInfo = CATEGORIES.find(c => c.id === intent.category);
      return {
        text: `${catInfo?.icon || '✨'} यहाँ हैं कुछ ${catInfo?.name?.[lang] || intent.category} शायरी:`,
        shayaris: picks,
        chips: [
          { label: '🔄 और दिखाओ', msg: `more ${intent.category} shayari` },
          { label: '🎲 Random', msg: 'random shayari' },
        ],
      };
    }

    case 'mood': {
      const pool = SHAYARI.filter(s => intent.categories.includes(s.category));
      const picks = pickRandom(pool, 3);
      return {
        text: `${intent.mood === 'happy' ? '😊' : intent.mood === 'sad' ? '💔' : '✨'} आपके mood के लिए:`,
        shayaris: picks,
        chips: [{ label: '🔄 और दिखाओ', msg: `${intent.mood} mood shayari` }],
      };
    }

    case 'poet': {
      const pool = SHAYARI.filter(s =>
        s.author?.en?.toLowerCase().includes(intent.poet.toLowerCase()) ||
        s.author?.hi?.includes(intent.poet) ||
        s.author?.ur?.includes(intent.poet)
      );
      if (!pool.length) {
        return {
          text: `😔 "${intent.poet}" की शायरी नहीं मिली। दूसरे शायर का नाम बताएँ।`,
          shayaris: [],
          chips: Object.keys(POET_KEYWORDS).slice(0, 5).map(p => ({ label: p, msg: p + ' ki shayari' })),
        };
      }
      const picks = pickRandom(pool, 3);
      return {
        text: `✍️ **${intent.poet}** की शायरी:`,
        shayaris: picks,
        chips: [{ label: '🔄 और', msg: intent.poet + ' ki shayari' }],
      };
    }

    case 'occasion': {
      const tagMap = {
        birthday: ['birthday','janamdin'],
        wedding:  ['wedding','shaadi'],
        eid:      ['eid'],
        diwali:   ['diwali'],
        holi:     ['holi'],
        mothers:  ['mother','maa','माँ'],
        fathers:  ['father','papa'],
        independence: ['patriotic','independence'],
      };
      const tags = tagMap[intent.occasion] || [];
      const pool = SHAYARI.filter(s =>
        tags.some(t => (s.tags || []).some(st => st.toLowerCase().includes(t))) ||
        tags.some(t => s.hi.toLowerCase().includes(t)) ||
        (intent.occasion === 'mothers' && s.category === 'hearttouching') ||
        (intent.occasion === 'independence' && s.category === 'patriotic')
      );
      const picks = pickRandom(pool.length ? pool : SHAYARI.filter(s => s.category === 'hearttouching'), 3);
      return {
        text: `🎉 **${intent.occasion}** के लिए शायरी:`,
        shayaris: picks,
        chips: [{ label: '🔄 और दिखाओ', msg: intent.occasion + ' shayari' }],
      };
    }

    case 'popular': {
      const sorted = [...SHAYARI].sort((a, b) => (b.likes || 0) - (a.likes || 0));
      return {
        text: '🔥 **सबसे लोकप्रिय शायरी:**',
        shayaris: sorted.slice(0, 3),
        chips: [{ label: '🔄 और Popular', msg: 'popular shayari' }],
      };
    }

    case 'random': {
      const picks = pickRandom(SHAYARI, 3);
      return {
        text: '🎲 **Random शायरी:**',
        shayaris: picks,
        chips: [
          { label: '🔄 और Random', msg: 'random shayari' },
          { label: '❤️ Love', msg: 'love shayari' },
          { label: '💔 Sad', msg: 'sad shayari' },
        ],
      };
    }

    case 'explain': {
      return {
        text: '💡 किस शायरी का मतलब जानना है? उसे यहाँ paste करें या Copy button के बाद यहाँ भेजें — मैं उसका अर्थ समझाऊँगा।',
        shayaris: [],
        chips: [{ label: '🎲 Random', msg: 'random shayari' }],
      };
    }

    case 'search':
    default: {
      const q = intent.query?.toLowerCase() || '';
      if (!q || q.length < 2) {
        return {
          text: '🤔 मुझे समझ नहीं आया। नीचे दिए options में से चुनें:',
          shayaris: [],
          chips: QUICK_CHIPS.slice(0, 6),
        };
      }
      const pool = SHAYARI.filter(s =>
        s.hi.toLowerCase().includes(q) ||
        s.en.toLowerCase().includes(q) ||
        (s.ur && s.ur.toLowerCase().includes(q)) ||
        (s.author?.en || '').toLowerCase().includes(q) ||
        (s.tags || []).some(t => t.toLowerCase().includes(q))
      );
      if (!pool.length) {
        return {
          text: `😔 "${intent.query}" के लिए कोई शायरी नहीं मिली।\nकोई category या mood बताएँ:`,
          shayaris: [],
          chips: QUICK_CHIPS.slice(0, 5),
        };
      }
      const picks = pickRandom(pool, 3);
      return {
        text: `🔍 "${intent.query}" के लिए **${pool.length}** शायरी मिलीं:`,
        shayaris: picks,
        chips: [{ label: '🔄 और दिखाओ', msg: intent.query }],
      };
    }
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <div className="cb-typing">
      <span /><span /><span />
    </div>
  );
}

function ShayariMessage({ shayari, language, onFav, onCopy, isFav }) {
  const lang = language || 'hi';
  const text = lang === 'ur' ? (shayari.ur || shayari.hi) : lang === 'en' ? shayari.en : shayari.hi;
  const author = shayari.author?.[lang] || shayari.author?.en || 'Unknown';
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(text + '\n— ' + author).then(() => {
      setCopied(true);
      onCopy();
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ text: text + '\n— ' + author, title: 'Shayari Studio' });
    } else {
      handleCopy();
    }
  };

  return (
    <div className="cb-shayari-card">
      <p className={`cb-shayari-text ${lang === 'ur' ? 'urdu-text' : lang === 'hi' ? 'hindi-text' : 'english-text'}`}>
        {text}
      </p>
      <p className="cb-shayari-author">— {author}</p>
      <div className="cb-shayari-actions">
        <button className={`cb-action-btn ${isFav ? 'cb-action-btn--active' : ''}`} onClick={() => onFav(shayari.id)} title="Favorite">
          {isFav ? '❤️' : '🤍'}
        </button>
        <button className="cb-action-btn" onClick={handleCopy} title="Copy">
          {copied ? '✅' : '📋'}
        </button>
        <button className="cb-action-btn" onClick={handleShare} title="Share">
          📤
        </button>
      </div>
    </div>
  );
}

function Message({ msg, language, onFav, onCopy, isFavorite }) {
  const isBot = msg.role === 'bot';

  const formatText = (text) => {
    return text.split('\n').map((line, i) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <span key={i} dangerouslySetInnerHTML={{ __html: formatted }} style={{ display: 'block' }} />;
    });
  };

  return (
    <div className={`cb-msg ${isBot ? 'cb-msg--bot' : 'cb-msg--user'}`}>
      {isBot && <div className="cb-avatar">🌙</div>}
      <div className="cb-bubble-wrap">
        {msg.text && (
          <div className={`cb-bubble ${isBot ? 'cb-bubble--bot' : 'cb-bubble--user'}`}>
            {formatText(msg.text)}
          </div>
        )}
        {msg.shayaris?.map(s => (
          <ShayariMessage
            key={s.id}
            shayari={s}
            language={language}
            onFav={onFav}
            onCopy={onCopy}
            isFav={isFavorite(s.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main ChatBot Component ──────────────────────────────────────────────────

export default function ChatBot() {
  const { language, toggleFavorite, isFavorite, showToast } = useApp();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [chips, setChips] = useState(QUICK_CHIPS.slice(0, 6));
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Initial greeting
  useEffect(() => {
    const greeting = buildResponse({ type: 'greet' }, language);
    setMessages([{ id: 1, role: 'bot', text: greeting.text, shayaris: [] }]);
    setChips(greeting.chips);
  }, [language]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const userMsg = { id: Date.now(), role: 'user', text: trimmed, shayaris: [] };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setTyping(true);
    setChips([]);

    await new Promise(r => setTimeout(r, 700 + Math.random() * 500));

    const intent = detectIntent(trimmed);
    const response = buildResponse(intent, language);

    setTyping(false);
    const botMsg = { id: Date.now() + 1, role: 'bot', ...response };
    setMessages(prev => [...prev, botMsg]);
    setChips(response.chips || []);

    if (!open) setUnread(u => u + 1);
  }, [language, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleFav = (id) => {
    toggleFavorite(id);
  };

  const handleCopy = () => {
    showToast('📋 Copied to clipboard!');
  };

  return (
    <>
      {/* ── Floating Button ── */}
      <button
        className={`cb-fab ${open ? 'cb-fab--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Chat with AI"
      >
        {open ? '✕' : '🤖'}
        {!open && unread > 0 && <span className="cb-badge">{unread}</span>}
      </button>

      {/* ── Chat Window ── */}
      {open && (
        <div className="cb-window">
          {/* Header */}
          <div className="cb-header">
            <div className="cb-header-info">
              <span className="cb-header-avatar">🌙</span>
              <div>
                <div className="cb-header-name">Shayari AI</div>
                <div className="cb-header-status">
                  <span className="cb-online-dot" /> ऑनलाइन • 2295 शायरी
                </div>
              </div>
            </div>
            <button className="cb-close-btn" onClick={() => setOpen(false)}>✕</button>
          </div>

          {/* Messages */}
          <div className="cb-messages">
            {messages.map(msg => (
              <Message
                key={msg.id}
                msg={msg}
                language={language}
                onFav={handleFav}
                onCopy={handleCopy}
                isFavorite={isFavorite}
              />
            ))}
            {typing && (
              <div className="cb-msg cb-msg--bot">
                <div className="cb-avatar">🌙</div>
                <div className="cb-bubble-wrap">
                  <div className="cb-bubble cb-bubble--bot">
                    <TypingDots />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick Chips */}
          {chips.length > 0 && (
            <div className="cb-chips">
              {chips.map((chip, i) => (
                <button key={i} className="cb-chip" onClick={() => sendMessage(chip.msg)}>
                  {chip.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form className="cb-input-area" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              className="cb-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="शायरी पूछें... (love, sad, Ghalib...)"
              autoComplete="off"
            />
            <button className="cb-send-btn" type="submit" disabled={!input.trim()}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </form>

          <div className="cb-footer-note">Powered by Shayari Studio AI · 2295+ शायरी</div>
        </div>
      )}
    </>
  );
}
